/**
 * The app: what the parts have to do with one another.
 *
 * Here stands what used to be scattered everywhere — the flow of checking,
 * the saving, the opening of a text, the photo. The components below do not
 * know each other; they read signals and report back.
 */

import { useRef, useEffect, useCallback } from 'preact/hooks'
import { useSignalEffect } from '@preact/signals'
import * as st from './state.js'
import * as store from './editor/store.js'
import * as text from './editor/text.js'
import {
  takeKeyFromUrl, apiKey, startSync, whoAmI, lastName, lastView, signOut,
  deviceWasWiped, DRAFT_STORE,
} from './editor/sync.js'
import { createChecker } from './spell/check.js'
import { modelFindings, withoutOverlap } from './spell/fromModel.js'
import {
  insertionAt, removalAt, spacingRule, whatIsMissing, cleanMessage, isWordFinding,
  around, preview, trackCorrections, setStage, clearStages, isPunctuation,
} from './spell/rules.js'
import { Bar } from './ui/Bar.js'
import { Editor } from './ui/Editor.js'
import { Helper } from './ui/Helper.js'
import { Exercise } from './ui/Exercise.js'
import { Start } from './ui/Start.js'
import { Stats } from './ui/Stats.js'
import { SignIn } from './ui/SignIn.js'
import { PhotoStart } from './ui/PhotoStart.js'
import { PhotoPanel } from './ui/PhotoPanel.js'
import { Reviewing } from './ui/Reviewing.js'
import { allIssues, withoutSentence, contextFor, type Issue } from './exercise/exercises.js'
import { dueForReview, markReviewed, markAllReviewed, forgetGone } from './exercise/review.js'
import { typed, stoppedWriting, noteCleanWords, cleanWords, notePhoto } from './exercise/time.js'
import { photo } from './ui/PhotoPanel.js'
import { BUILTIN_FONTS, DEFAULT_FONT } from './ui/paper.js'
import { applyPaper } from './editor/geometry.js'
import type { Doc, Finding, PhotoResult, MoreExercise, RawMistake, Mistake, Fixes } from './types.js'

/** The draft in the browser — it secures the time between two saves. */
const STORE = DRAFT_STORE

/** Fonts the browser already knows. Registering twice does harm. */
const loaded = new Set<string>()

export function App() {
  const input = useRef<HTMLDivElement>(null)

  /** The findings of the last check, before the typing filter. */
  const lastFindings = useRef<Finding[]>([])
  /**
   * What the model found in the written texts, for marking them in the text.
   *
   * Kept here rather than fetched on every check: the check runs on every
   * keystroke, and reading the store each time would cost more than it
   * saves. Refilled after a reading and after a sync.
   */
  const modelHits = useRef<Mistake[]>([])
  const lastCheckedText = useRef<string | null>(null)
  const checkRun = useRef(0)
  const inflight = useRef<AbortController | null>(null)
  /** Where typing happens right now — the word there stays unmarked. */
  const typedAt = useRef<number | null>(null)

  /**
   * Words that were really visible with a squiggly underline.
   *
   * The raw finding is NOT enough as evidence: between checking and display
   * lies the typing filter that leaves the word under the caret alone. A word
   * that stood only in the raw findings was never marked — and whoever
   * finishes typing it in the same breath corrects nothing, he writes.
   */
  const shown = useRef(new Set<string>())

  const syncTimer = useRef<number>()
  const runSync = useRef<() => Promise<void>>()
  const autosave = useRef<number>()
  const checkTimer = useRef<number>()
  /** Is a fetch of exercise sentences running? Two at once would be money. */
  const fetchingExercises = useRef(false)
  /** Mistakes already sent for sentences in this session — see `withoutSentence`. */
  const triedExercises = useRef(new Set<string>())

  const getText = (): string => (input.current ? text.getText(input.current) : '')

  // --- Saving ---------------------------------------------------------------

  /** The first non-empty line is the title. There is no field for it. */
  const titleFromText = (): string => {
    const line = getText().split('\n').find((z) => z.trim()) ?? ''
    const t = line.trim()
    if (t.length <= 60) return t
    const cut = t.slice(0, 60)
    const gap = cut.lastIndexOf(' ')
    return (gap > 20 ? cut.slice(0, gap) : cut) + '…'
  }

  /**
   * What was put right in this text and not saved yet.
   *
   * Counted here and handed to the text on its next save, because the first
   * corrections come before the text has a row of its own.
   */
  const unsavedFixes = useRef<Fixes>({ solo: 0, help: 0 })

  const currentData = () => {
    const had = st.doc.value?.fixes ?? { solo: 0, help: 0 }
    const add = unsavedFixes.current
    return {
      title: titleFromText(),
      text: getText(),
      personal: st.personal.value,
      fixes: { solo: had.solo + add.solo, help: had.help + add.help },
    }
  }

  /**
   * The draft in the browser.
   *
   * It does not replace saving, it secures the time IN BETWEEN. Whoever
   * closes the lid finds the text again at the next opening.
   */
  const saveDraft = (): void => {
    st.dirty.value = true
    try {
      localStorage.setItem(STORE, JSON.stringify({
        text: getText(),
        title: titleFromText(),
        personal: st.personal.value,
        checking: st.checking.value,
        commas: st.commas.value,
        docId: st.doc.value?.id ?? null,
        paper: st.paper.value,
        printBg: st.printBg.value,
        fontId: st.fontId.value,
        gridSpacing: st.gridSpacing.value,
      }))
    } catch {
      // Private mode — the text is lost on closing, but the app must run
      // nevertheless.
    }
  }

  /** Adds the id of the text to the draft, without making it dirty. */
  const rememberDocId = (): void => {
    try {
      const raw = localStorage.getItem(STORE)
      const d = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
      d.docId = st.doc.value?.id ?? null
      localStorage.setItem(STORE, JSON.stringify(d))
    } catch {}
  }

  const syncSoon = (): void => {
    clearTimeout(syncTimer.current)
    syncTimer.current = setTimeout(() => void runSync.current?.(), 2000)
  }

  /**
   * Saves the text. No dialog, never.
   *
   * There is no file for which one would have to choose a place. The text
   * belongs to Elmar, not to a folder.
   */
  const saveDoc = useCallback(async (): Promise<boolean> => {
    // A parent only reads. The paper is locked for him, so this is the
    // second lock — the one behind a draft that found another way in.
    if (st.isParent()) return false
    // An empty sheet that was never saved is not a text.
    if (!getText().trim() && !st.doc.value) return false
    try {
      st.doc.value = await store.saveDoc(st.doc.value, currentData())
      unsavedFixes.current = { solo: 0, help: 0 }
      st.dirty.value = false
      // Add the row id to the draft.
      //
      // `saveDraft` writes it from `st.doc.value`, and that is still null at
      // the first save — the draft pointed to nothing afterwards, and the
      // next start created a second row for the same text.
      rememberDocId()
      return true
    } catch (err) {
      console.error(err)
      st.toast.value = 'Speichern hat nicht geklappt.'
      return false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Checking -------------------------------------------------------------

  /**
   * What of the findings really gets marked.
   *
   * Unfinished is only the word in which the caret still stands exactly
   * where the last keystroke left it. Every caret movement — click, arrow
   * key, selection — thus makes the word finished automatically.
   */
  const applyFindings = useCallback((): void => {
    const el = input.current
    const c = el ? text.caret(el) : null
    const beingTyped = (e: Finding): boolean =>
      typedAt.current !== null && c !== null &&
      c.start === typedAt.current && c.end === typedAt.current &&
      c.start > e.start && c.start <= e.end

    const visible = lastFindings.current.filter((e) => !beingTyped(e))
    st.errors.value = visible
    // Record what Elmar actually saw in red. Only that counts later as a
    // correction — whoever corrects himself while typing has put nothing
    // right, he has made a typing slip.
    for (const e of visible) shown.current.add(e.word.toLowerCase())
  }, [])

  const check = useCallback(async (): Promise<void> => {
    if (!st.checker || !st.checking.value) {
      lastFindings.current = []
      lastCheckedText.current = null
      applyFindings()
      return
    }

    const t = getText()

    // Unchanged text: only apply the typing filter again. A selection or a
    // click changes nothing in the text and may therefore neither trigger a
    // request nor let the marks disappear in the meantime.
    if (t === lastCheckedText.current) {
      applyFindings()
      return
    }

    const run = ++checkRun.current

    // Abort the running request: while typing every answer would otherwise be
    // a candidate, and the slowest would arrive last.
    inflight.current?.abort()
    const ctrl = new AbortController()
    inflight.current = ctrl

    let found: Finding[]
    try {
      found = await st.checker.check(t, { signal: ctrl.signal })
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      found = []
    }

    // An older answer may not overwrite a newer result.
    if (run !== checkRun.current) return
    if (getText() !== t) return

    // What the MODEL found, alongside what the checker found.
    //
    // The two see different things. LanguageTool walks past every mistake
    // that is a real word in the wrong place — „den" for „denn" — and those
    // are exactly what the model is asked about on the start screen. Its
    // answers were stored as exercises and never shown in the text, which is
    // the place where he would recognise them as his own.
    //
    // Added, not merged: where both mark the same word the checker wins,
    // because it knows the offset from the text rather than from a search.
    found = [...found, ...withoutOverlap(modelFindings(t, modelHits.current), found)]

    // What has disappeared since the last check? Those are the corrections.
    for (const b of trackCorrections(lastFindings.current, lastCheckedText.current, found, t, shown.current)) {
      if (b.stage === 0) unsavedFixes.current.solo += 1
      else unsavedFixes.current.help += 1
    }

    lastFindings.current = found
    lastCheckedText.current = t
    applyFindings()

    // How many words stand there without a mark under them.
    //
    // Right here, because this is the moment the answer is complete: the
    // text and its findings belong to each other. Counted per day as a
    // high-water mark, so a text typed, corrected and typed on does not
    // count its sentences over and over — see `noteCleanWords`.
    noteCleanWords(cleanWords(t, new Set(found.map((f) => f.word.toLowerCase()))))

    // The service can fail at any time, not only at the start — and come back.
    st.checkWarning.value = st.checker.online === false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFindings])

  /** The comma switch and the check switch take effect immediately. */
  useSignalEffect(() => {
    void st.checking.value
    void st.commas.value
    void check()
  })

  // --- The text in the field ------------------------------------------------

  /** Puts a text on the paper. Only when loading, never when rendering. */
  const showDoc = useCallback((d: Doc | null, data: { text: string; personal?: string[] }): void => {
    const el = input.current
    if (!el) return
    // The evidence applies per session on the text: another text brings its
    // own words, and what was marked there must show itself.
    shown.current.clear()
    clearStages()
    unsavedFixes.current = { solo: 0, help: 0 }
    st.doc.value = d
    text.setText(el, data.text ?? '')
    if (data.personal?.length) {
      // The dictionary of the text adds to the own one, it does not replace
      // it: names that already count as correct here shall stay so.
      st.personal.value = [...new Set([...st.personal.value, ...data.personal])]
      st.checker?.setPersonal(st.personal.value)
    }
    st.dirty.value = false
    st.session.value = null
    lastCheckedText.current = null
    void check()
  }, [check])

  const onInput = (e: InputEvent): void => {
    saveDraft()
    // Writing counts as practice, and it is the main form of it. Booked per
    // keystroke, not per session: a gap longer than a minute is a break and
    // is dropped — see `typed` in exercise/time.ts.
    typed()

    const ch = e.data
    // An inserted character means: writing is happening here right now. A
    // finishing character ends the word again.
    const FINISHES = /[\s.,;:!?"'()\-–—]/
    const el = input.current
    typedAt.current = ch && !FINISHES.test(ch)
      ? (el ? text.caret(el)?.start ?? null : null)
      : null

    // Check immediately after a finishing character — that is the immediate
    // feedback this is all about. The same after deleting: whoever removes a
    // letter has usually just corrected something and wants to see whether it
    // is right now. Otherwise only after a short pause.
    const deleted = e.inputType?.startsWith('delete')
    if ((ch && FINISHES.test(ch)) || deleted) {
      void check()
    } else {
      clearTimeout(checkTimer.current)
      checkTimer.current = setTimeout(() => void check(), 400)
    }

    clearTimeout(autosave.current)
    autosave.current = setTimeout(async () => {
      if (await saveDoc()) syncSoon()
    }, 1200)
  }

  // --- The help -------------------------------------------------------------

  const openHelper = (hit: Finding): void => {
    st.session.value = {
      word: hit.word,
      start: hit.start,
      end: hit.end,
      finding: hit,
      stage: 0,
      punctuation: isPunctuation(hit),
    }
  }

  /** Inserts the word — through execCommand, so that undo stays available. */
  const applyWord = (word: string): void => {
    const el = input.current
    const s = st.session.value
    if (!el || !s) return

    const spot = insertionAt(s.finding)
    const target = spot ? s.start + spot.offset : s.start
    const to = spot ? target : s.end

    // Remember the help stage before the session closes: the correction is
    // recognized only at the next check run, and by then it is already gone.
    setStage(s.word.toLowerCase(), s.stage)

    text.setCaret(el, target, to)
    const inserted = spot ? spot.char : word
    text.insertText(el, inserted)

    // The range of the NEW word, not of the old one: „mude" is four
    // characters long, „müde" as well — but with an insertion everything
    // shifts by one character, and the green would sit beside it.
    st.markSolved(s.start, spot ? target + 1 : s.start + inserted.length)
    setTimeout(() => st.unmarkSolved(s.start), 1500)
    st.session.value = null
    saveDraft()
    void check()
  }

  /** Select the word and let him try himself. */
  const retryWord = (): void => {
    const el = input.current
    const s = st.session.value
    if (!el || !s) return
    el.focus()
    text.setCaret(el, s.start, s.end)
    st.session.value = null
  }

  // --- The photo ------------------------------------------------------------

  const photoFile = useRef<HTMLInputElement>(null)

  const openPhoto = (): void => {
    if (!apiKey()) {
      st.toast.value = 'Dafür braucht die App ihren Schlüssel — öffne einmal den Link mit #k=…'
      return
    }
    if (photoFile.current) {
      photoFile.current.value = ''
      photoFile.current.click()
    }
  }

  /**
   * Puts a photographed page on the paper.
   *
   * It is stored as a text, so this is the ordinary way of opening one — the
   * page id IS the text id. Only the fallback is special: a page from before
   * this change has no text yet, so one is made on the spot.
   */
  const openPage = async (id: string): Promise<void> => {
    let d = await store.getDoc(id).catch(() => null)
    if (!d) {
      const page = (await store.pages().catch(() => [])).find((p) => p.id === id)
      if (!page) return
      d = await store.docFromPage(page).catch(() => null)
    }
    if (!d) return
    // The screen FIRST, then the text.
    //
    // `showDoc` writes into the editor and gives up when it is not there —
    // and on the photo screen it is not mounted at all. Setting the text
    // before the switch left the paper blank, silently: the field simply did
    // not exist yet. The timeout gives Preact its render before the write.
    st.goTo('paper')
    const doc = d
    setTimeout(() => {
      showDoc(doc, store.readDoc(doc))
      input.current?.focus()
    }, 0)
  }

  /**
   * Reads one page: the photo to the server, the transcript into the store.
   *
   * @returns the text the page became, or null when it could not be stored.
   *   Throws when the server said no — the caller decides what the screen
   *   says, because it knows whether this was page one of one or three of
   *   four.
   */
  const readPage = async (file: File): Promise<Doc | null> => {
    const image = await downscale(file, 2400)
    const res = await fetch('/api/photo', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-kritzl-key': apiKey() },
      body: JSON.stringify({ image: image.data, mediaType: image.type }),
    })
    const out = (await res.json().catch(() => ({}))) as PhotoResult & { error?: string }
    if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`)

    // The SERVER's id and time, not fresh ones.
    //
    // The server has already written the page and its mistakes under its
    // own id. A second id made here meant a second page: the local one
    // went up with the next sync, the server's came down, and every photo
    // stood twice in the database — the same six findings under two page
    // ids, in his exercises only kept apart by the round's deduplication.
    // One id on both sides, and the sync finds the row already there.
    const page = {
      id: out.pageId || `foto-${Date.now().toString(36)}`,
      text: out.text,
      at: out.at || Date.now(),
    }
    await store.addMistakes(page, out.mistakes).catch(() => 0)
    // The transcript becomes a TEXT, not just a stored page: from here on
    // a photographed page and a typed one are the same thing — openable,
    // correctable, printable in his own handwriting.
    const doc = await store.docFromPage(page).catch(() => null)
    // The model has just read this page. Without the note, the start
    // screen would count its text as unread and have it read a second
    // time — and every mistake on it would be stored twice.
    if (doc) await markReviewed(doc.id, doc.rev)
    // A photograph has no duration, so it is counted rather than timed —
    // see `notePhoto`. Without it a day spent photographing a whole
    // exercise book would look empty in the week. Not for a parent: the
    // week shows what the child did.
    if (!st.isParent()) notePhoto()
    return doc
  }

  /**
   * Sends the photographed pages, one after the other.
   *
   * Several at once, because a class test has four sheets and an exercise
   * book has many pages — and whoever uploads from the PC has the photos
   * already. On the phone the camera gives one at a time, and that stays
   * as it is.
   *
   * In sequence, not in parallel: each one costs a call and twenty
   * seconds, and four at once would be four times the load for no gain in
   * what the child sees — the pages come in the order they were taken.
   */
  const sendPhotos = async (files: File[]): Promise<void> => {
    // One batch at a time. The same page once came in twice, 63 ms apart —
    // two pages, every mistake on it twice, at twice the price.
    if (photo.value?.kind === 'wait' || !files.length) return
    st.panel.value = 'photo'
    const total = files.length
    let last: Doc | null = null
    for (const [i, file] of files.entries()) {
      photo.value = { kind: 'wait', nr: i + 1, total }
      try {
        last = await readPage(file)
      } catch (err) {
        // Stop at the page that failed — the ones before it are in, the
        // ones after it are not, and the message says which one it was,
        // so the right one can be taken again.
        const message = (err as Error).message
        photo.value = {
          kind: 'error',
          message: total > 1 ? `Seite ${i + 1} von ${total}: ${message}` : message,
        }
        void countOpenIssues()
        syncSoon()
        return
      }
    }

    photo.value = null
    st.panel.value = null
    if (total === 1 && last) {
      // Straight onto the paper.
      //
      // The card that used to stand in between showed the transcript and
      // asked whether to take it over — a question with one sensible answer,
      // in front of the very thing it was describing. The page is now a text
      // like any other, so it simply opens, and what the model found is
      // marked in it where a mistake actually stands.
      const doc = last
      st.goTo('paper')
      setTimeout(() => {
        showDoc(doc, store.readDoc(doc))
        input.current?.focus()
      }, 0)
    } else {
      // Several pages: not the last one on the paper, but the list, where
      // all of them now stand. Opening one of four would hide the other
      // three, and the list is where one sees what came of each.
      st.goTo('photo')
    }
    void countOpenIssues()
    syncSoon()
  }

  // --- Start ----------------------------------------------------------------

  useEffect(() => {
    takeKeyFromUrl()

    // Whose app is this? Before anything else is shown.
    //
    // Asked against the server, not merely "is a key stored": a key that was
    // withdrawn, or one that was mistyped into the field of another device,
    // looks exactly like a good one in localStorage.
    //
    // No network is NOT a refusal. Whoever practises on the train would
    // otherwise be thrown out of an app that works perfectly well offline —
    // so a stored key counts as good until the server says otherwise.
    void (async () => {
      try {
        const name = await whoAmI()
        // The key now opens somebody else's Kritzl, and the device has just
        // been emptied for it. Start over, so that nothing on the screen —
        // the paper, the texts, a round of exercises — is from before.
        if (deviceWasWiped()) return location.reload()
        st.user.value = name === null ? null : { name, ...lastView() }
        // A key the server refuses is thrown away — it was withdrawn, or it
        // never was one. Keeping it would have the background sync knock
        // with it every two minutes for a 401.
        if (name === null) signOut()
      } catch {
        // No network is not a refusal — the name from the last check keeps
        // the greeting right on a train.
        st.user.value = apiKey() ? { name: lastName(), ...lastView() } : null
      } finally {
        st.checkedKey.value = true
      }
    })()

    // The draft onto the paper, before anything else happens.
    let docId: string | null = null
    try {
      const raw = localStorage.getItem(STORE)
      if (raw) {
        const d = JSON.parse(raw) as Record<string, unknown>
        if (typeof d.text === 'string' && input.current) text.setText(input.current, d.text)
        if (Array.isArray(d.personal)) st.personal.value = d.personal as string[]
        if (typeof d.checking === 'boolean') st.checking.value = d.checking
        if (typeof d.commas === 'boolean') st.commas.value = d.commas
        if (typeof d.paper === 'string') st.paper.value = d.paper
        if (typeof d.printBg === 'boolean') st.printBg.value = d.printBg
        if (typeof d.fontId === 'string') st.fontId.value = d.fontId
        if (typeof d.gridSpacing === 'boolean') st.gridSpacing.value = d.gridSpacing
        docId = typeof d.docId === 'string' ? d.docId : null
      }
    } catch {}

    void (async () => {
      // On the very first run: everything already written counts as read.
      //
      // Without this, the day this was built every text ever typed would be
      // sent to the model one after another — eighteen of them in the test
      // account, at a call each, for findings that were collected long ago.
      try {
        if (!localStorage.getItem('kritzl.reviewStarted')) {
          await markAllReviewed()
          localStorage.setItem('kritzl.reviewStarted', '1')
        }
      } catch {}

      try {
        st.fonts.value = await store.fonts()
        const known =
          BUILTIN_FONTS.some((f) => f.id === st.fontId.value) ||
          st.fonts.value.some((f) => f.id === st.fontId.value)
        if (!known) st.fontId.value = DEFAULT_FONT.id
      } catch {
        st.fonts.value = []
      }

      // Find the text last open again. Only the row, not the content: the
      // draft is already on the paper and is the newer one.
      if (docId) {
        try {
          const d = await store.getDoc(docId)
          if (d && !d.deleted) st.doc.value = d
        } catch {}
      }

      try {
        const c = await createChecker()
        st.setChecker(c)
        c.setPersonal(st.personal.value)
        await check()
      } catch (err) {
        st.checkWarning.value = true
        console.error(err)
      }

      runSync.current = startSync(() => {
        void refreshOpenDoc()
        void countOpenIssues()
      })
      void countOpenIssues()
    })()

    input.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Has the model read what he wrote?
   *
   * Runs when the start screen comes up — opening the app, coming back from
   * the paper, before tapping „Wiederholen". Every path goes through there,
   * and it is the one place that is not in the middle of something.
   *
   * Not on leaving the paper: whoever is finished writing may simply shut
   * the computer, and a screen being closed is no place to start a call that
   * costs money. Not while typing either, which is what it used to do — a
   * call per accepted correction, on fragments instead of the text.
   *
   * One text at a time. Several waiting is the rare case (a week away from
   * the tablet), and reading them one after another keeps each dialog short
   * rather than one long one.
   */
  const reviewing = useRef(false)

  const reviewWritten = async (): Promise<void> => {
    // A parent writes nothing, so nothing of the child's is waiting to be
    // read — and a text opened here must not be read as if he had.
    if (reviewing.current || !apiKey() || st.isParent()) return
    const [doc] = await dueForReview()
    if (!doc) { void forgetGone(); return }

    reviewing.current = true
    st.reviewing.value = true
    try {
      const res = await fetch('/api/review', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-kritzl-key': apiKey() },
        body: JSON.stringify({ text: doc.body }),
      })
      if (!res.ok) return
      const data = await res.json() as { mistakes?: RawMistake[] }

      // Noted BEFORE the mistakes are stored, not after.
      //
      // The call is paid for the moment the answer arrives. If storing then
      // fails — or the page is reloaded in between — a text marked
      // afterwards would come up as unread again and be paid for twice. The
      // worst case this way round is one lost set of findings; the other way
      // round it is a bill that repeats.
      await markReviewed(doc.id, doc.rev)

      // The text becomes a „page", exactly as a photographed one does. That
      // is the normalisation: from here on it makes no difference whether he
      // typed it or held a camera over it.
      //
      // Only what is NEW in this version. Whoever writes on in a text makes
      // it due again, and the model then finds the old mistakes a second
      // time — each reading was a page of its own, so every mistake stood
      // there once per version. The same for a photographed page: its text
      // is the page itself, and what the photo found is already stored.
      const before = (await store.mistakes().catch(() => []))
        .filter((x) => x.pageId === doc.id || x.pageId.startsWith(`text-${doc.id}-`))
      const known = new Set(before.map((x) => `${x.wrong.toLowerCase()}→${x.right.toLowerCase()}`))
      await store.addMistakes(
        { id: `text-${doc.id}-${doc.rev}`, text: doc.body, at: doc.changed },
        (data.mistakes ?? []).filter((x) => !known.has(`${x.wrong.toLowerCase()}→${x.right.toLowerCase()}`))
      )
      await countOpenIssues()
    } catch {
      // Away, slow, refused — the text stays unread and is offered again the
      // next time the start screen opens. Saying so would help nobody: there
      // is nothing he could do about it.
    } finally {
      reviewing.current = false
      st.reviewing.value = false
    }
  }

  /** How many mistakes are waiting — for the number on the practise button. */
  const countOpenIssues = async (): Promise<void> => {
    const [m, w] = await Promise.all([
      store.mistakes().catch(() => []),
      store.corrections().catch(() => []),
    ])
    const practised = new Set(w.filter((x) => x.practisedAt != null).map((x) => `w-${x.key}`))
    st.openIssues.value = allIssues(m, w, practised).length
    // The same rows serve the marks in the text — see `modelFindings`. Kept
    // here because this already reads them and runs after every sync.
    modelHits.current = m
    void check()
    // The exercise sentences only where the start screen is: this counting
    // also runs on every sync, and a sync happens while he is writing. That
    // is how sentences used to be fetched mid-text, one call at a time.
    if (st.screen.value === 'start') void fetchExercises(m)
  }

  /**
   * Fetches exercise sentences for mistakes that do not have one yet.
   *
   * In the background and quietly: whoever is writing right now notices
   * nothing of it, and if it does not work, the next sync tries again. A
   * mistake without a sentence does not appear in the exercises until then —
   * better no exercise than one on a sentence that is itself full of
   * mistakes.
   */
  const fetchExercises = async (
    m: Awaited<ReturnType<typeof store.mistakes>>
  ): Promise<void> => {
    if (!apiKey() || fetchingExercises.current) return

    // One request per SENTENCE, not per mistake.
    //
    // Several mistakes often stand in the same sentence — the model reads it
    // once and finds them all. Sending the same sentence twice would have it
    // write the same exercises over again, and the answer ran past
    // max_tokens.
    // Keyed by what is SENT: his sentence, or — for a word off a worksheet
    // that stood in no sentence — the word line. See `wordLine`.
    const bySentence = new Map<string, Issue[]>()
    for (const f of withoutSentence(m, triedExercises.current)) {
      const key = contextFor(f)
      const list = bySentence.get(key) ?? []
      list.push(f)
      bySentence.set(key, list)
    }
    // Six sentences at a time — see api/exercises.js.
    const sentences = [...bySentence.entries()].slice(0, 6)
    if (!sentences.length) return

    fetchingExercises.current = true
    for (const [, group] of sentences) for (const f of group) triedExercises.current.add(f.id)
    try {
      const res = await fetch('/api/exercises', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-kritzl-key': apiKey() },
        body: JSON.stringify({
          // The sentence from his own text, nothing else. The model reads it
          // and finds the mistakes itself — what the spell checker guessed
          // does not travel along, because it guesses badly at words it does
          // not know and that guess would end up in the exercise.
          items: sentences.map(([sentence, group]) => ({
            sentence,
            // Where the exercises may be stored. One sentence can hold
            // several mistakes; the server hands the exercises back under
            // these identifiers, as many as it found.
            //
            // `wrong` is only for matching the answer to the right mistake —
            // it does not go into the prompt. The model's own finding, from
            // the page or the review, so there is no checker's guess in it.
            targets: group.map((f) => ({ id: f.id, wrong: f.wrong })),
          })),
        }),
      })
      if (!res.ok) return
      const { exercises } = (await res.json()) as {
        exercises: Record<string, {
          sentence: string; wrong: string; right: string; kind?: string
          gen?: number; more?: MoreExercise[]; strategy?: string; tip?: string
        }>
      }

      // Add locally, so that the exercises have it at once.
      for (const [, group] of sentences) {
        for (const f of group) {
          const a = exercises[f.id]
          if (!a?.sentence) continue
          const fields = {
            sentence: a.sentence,
            sentenceWrong: a.wrong,
            sentenceRight: a.right,
            sentenceKind: a.kind,
            sentenceGen: a.gen ?? 0,
            sentenceMore: a.more ?? [],
            sentenceStrategy: a.strategy ?? '',
            sentenceTip: a.tip ?? '',
          }
          const issue = m.find((x) => x.id === f.id)
          if (issue) await store.putMistake({ ...issue, ...fields })
        }
      }
      await countOpenIssues()
    } catch {
      // No network, no key, model overloaded — the next sync tries again.
    } finally {
      fetchingExercises.current = false
    }
  }

  /** Take over a newer version of the open text from the sync. */
  const refreshOpenDoc = async (): Promise<void> => {
    const open = st.doc.value
    if (!open || st.dirty.value) return
    const d = await store.getDoc(open.id).catch(() => null)
    if (!d || (d.rev ?? 1) <= (open.rev ?? 1)) return
    if (d.deleted) { st.doc.value = null; return }
    showDoc(d, store.readDoc(d))
    st.toast.value = 'Der Text wurde auf einem anderen Gerät geändert.'
  }

  /**
   * Ends the writing stretch when the paper is left.
   *
   * Without this the gap between the last keystroke and the return to the
   * paper would be measured as writing — a minute of it, every time. The
   * same on hiding the tab: whoever switches away is not writing.
   */
  useSignalEffect(() => {
    if (st.screen.value !== 'paper' || st.exercise.value) stoppedWriting()
  })

  useEffect(() => {
    const away = (): void => { if (document.hidden) stoppedWriting() }
    document.addEventListener('visibilitychange', away)
    return () => document.removeEventListener('visibilitychange', away)
  }, [])

  /**
   * The browser's back button.
   *
   * Without it, back leads out of the app — and on a phone that is the
   * gesture one makes without thinking. `goTo` pushes an entry for every
   * screen; this reads them back.
   *
   * `push: false`, because going back must not add another entry: otherwise
   * every step back would leave a step forward behind and one could never
   * leave.
   *
   * No entry at all means the very first screen — the state is empty for the
   * page as it was loaded.
   */
  useEffect(() => {
    const back = (e: PopStateEvent): void => {
      const s = (e.state as { screen?: st.Screen } | null)?.screen
      st.goTo(s ?? 'start', false)
    }
    addEventListener('popstate', back)
    return () => removeEventListener('popstate', back)
  }, [])

  /**
   * Paper and font onto the root element.
   *
   * Hangs on all five signals that determine the geometry. Before, this was
   * `applyPaper()`, which had to be called by hand in six places — and one
   * forgotten place meant: the sheet kept the measures of the previous one.
   */
  useSignalEffect(() => {
    applyPaper({
      paper: st.currentPaper.value,
      paperId: st.paper.value,
      family: currentFamily(),
      printBg: st.printBg.value,
      gridSpacing: st.gridSpacing.value,
    })
  })

  /**
   * Registers the chosen font with the browser.
   *
   * An own font lies as a file in IndexedDB and is unknown to the browser
   * until it is registered here.
   */
  useSignalEffect(() => {
    const id = st.fontId.value
    const own = st.fonts.value.find((f) => f.id === id)
    void (async () => {
      if (own) {
        if (loaded.has(own.family)) return
        try {
          const face = new FontFace(own.family, own.data)
          await face.load()
          document.fonts.add(face)
          loaded.add(own.family)
        } catch (err) {
          console.error('Font cannot be loaded:', own.name, err)
          st.toast.value = `„${own.name}" lässt sich nicht als Schrift lesen.`
        }
        return
      }
      const builtin = BUILTIN_FONTS.find((f) => f.id === id) ?? DEFAULT_FONT
      if (loaded.has(builtin.family)) return
      try {
        await document.fonts.load(`40px "${builtin.family}"`)
        loaded.add(builtin.family)
      } catch {
        // Without the metrics the size from the paper table applies — it is
        // calculated for Atkinson and still readable.
      }
    })()
  })

  // The hint disappears by itself.
  useSignalEffect(() => {
    if (!st.toast.value) return
    const id = setTimeout(() => (st.toast.value = ''), 2600)
    return () => clearTimeout(id)
  })

  /**
   * Printing: the marks must go away before.
   *
   * Not through a state change shortly before `print()` — that would be
   * rendered only after the printing, and the fair copy would come out of the
   * printer full of red waves. `beforeprint` runs early enough, `afterprint`
   * brings them back.
   */
  useEffect(() => {
    const clear = (): void => { CSS.highlights?.clear() }
    const restore = (): void => { void check() }
    addEventListener('beforeprint', clear)
    addEventListener('afterprint', restore)
    return () => {
      removeEventListener('beforeprint', clear)
      removeEventListener('afterprint', restore)
    }
  }, [check])

  const fontFamily = currentFamily()

  /**
   * The hidden file field the camera hangs on.
   *
   * Written once and put into EVERY branch below. It stood only at the end of
   * the main return, and the photo screen returns before ever reaching it —
   * so `photoFile.current` was null there and tapping „Seite fotografieren"
   * did nothing at all. Silently: `openPhoto` checks the ref and gives up.
   */
  const fileInput = (
    <input
      ref={photoFile}
      type="file"
      accept="image/*"
      capture="environment"
      // Several at once from a file picker. On the phone `capture` opens
      // the camera, which gives one picture — there `multiple` changes
      // nothing, and one page at a time is the natural way anyway.
      multiple
      hidden
      onChange={(e) => {
        const files = [...((e.currentTarget as HTMLInputElement).files ?? [])]
        if (files.length) void sendPhotos(files)
      }}
    />
  )

  // Nothing at all until the key has been looked at.
  //
  // Without this the sign-in screen would flash up on every start, in the
  // moment before the answer arrives — and that is the moment the child is
  // looking at the screen most closely.
  if (!st.checkedKey.value) return null

  // No key, no app. The texts belong to somebody, and without knowing to whom
  // there is nothing to open.
  if (!st.user.value) {
    return <SignIn onDone={(name) => {
      // A parent's key empties the device first; then the app starts over
      // as the child's mirror. See `whoAmI`.
      if (deviceWasWiped()) return location.reload()
      // With the view: a parent's key is a parent's key from the first
      // screen on, not only from the next start.
      st.user.value = { name, ...lastView() }
      // Fetch straight away what the server has for this child — otherwise
      // the start screen would stand empty until the first sync round.
      void runSync.current?.()
      void countOpenIssues()
    }} />
  }

  // The way in, and the detail behind it.
  //
  // Both lie BEFORE the paper and replace it: the editor keeps its text in
  // the DOM, so unmounting it would throw away the draft. Hence the paper
  // stays mounted and is merely hidden — see `.app-hidden` in style.css.
  if (st.screen.value === 'stats') {
    return (
      <>
        <Stats />
        {st.toast.value && <div class="toast">{st.toast.value}</div>}
      </>
    )
  }

  if (st.screen.value === 'photo') {
    return (
      <>
        <PhotoStart
          onTake={openPhoto}
          onClose={() => st.goTo('start')}
          onChanged={() => void countOpenIssues()}
          onOpen={(id: string) => void openPage(id)}
        />
        {/* The result lands in this card. It stands in the middle and belongs
            to no button — which is why it can be shown here as well as over
            the paper, without an anchor to hang from. */}
        <PhotoPanel
          onTakeOver={(t: string) => {
            st.goTo('paper')
            showDoc(null, { text: t })
            st.dirty.value = true
            saveDraft()
          }}
          onRetry={openPhoto}
        />
        {st.toast.value && <div class="toast">{st.toast.value}</div>}
        {fileInput}
      </>
    )
  }

  // Whenever the start screen comes up: is there a text the model has not
  // read? Also on the first load, which is what `screen` starts as.
  useSignalEffect(() => {
    if (st.screen.value === 'start' && !st.exercise.value) void reviewWritten()
  })

  const onPaper = st.screen.value === 'paper'

  return (
    <>
      {!onPaper && (
        <Start
          onWrite={() => {
            st.goTo('paper')
            // A fresh sheet, unless something is already on it: whoever taps
            // „Schreiben" wants to begin, not to find yesterday's text.
            if (!getText().trim()) {
              showDoc(null, { text: '' })
              try { localStorage.removeItem(STORE) } catch {}
            }
            setTimeout(() => input.current?.focus(), 0)
          }}
          onPractise={() => (st.exercise.value = true)}
          onPhoto={() => st.goTo('photo')}
        />
      )}

      {/* A strip at the bottom edge while the model reads a written text.
          Nothing to dismiss: it goes when the reading is done. */}
      {st.reviewing.value && <Reviewing />}

      <div class={onPaper ? undefined : 'app-hidden'}>
      <Bar
        onPrint={() => print()}
        onPick={(d: Doc) => showDoc(d, store.readDoc(d))}
        onNew={() => {
          showDoc(null, { text: '' })
          try { localStorage.removeItem(STORE) } catch {}
        }}
        onTakeOver={(t: string) => {
          showDoc(null, { text: t })
          st.dirty.value = true
          saveDraft()
        }}
        onRetry={openPhoto}
      />

      <Editor
        inputRef={input}
        onInput={onInput}
        onHelp={openHelper}
        fontFamily={fontFamily}
        readOnly={st.isParent()}
      />

      </div>

      <Helper
        session={st.session.value}
        text={getText()}
        inputRef={input}
        onClose={() => (st.session.value = null)}
        onApply={applyWord}
        onRetry={retryWord}
        onAdvance={(stage) => {
          const s = st.session.value
          if (s) st.session.value = { ...s, stage }
        }}
        helpers={{
          whatIsMissing,
          // The card only asks whether something is there — null and '' both mean no there.
          cleanMessage: (m?: string) => cleanMessage(m) ?? '',
          isWordFinding, insertionAt, removalAt, spacingRule, around, preview,
        }}
      />

      {st.exercise.value && <Exercise onSync={syncSoon} />}

      {st.toast.value && <div class="toast">{st.toast.value}</div>}

      {fileInput}
    </>
  )
}

/** The family in which the writing happens right now. */
function currentFamily(): string {
  const own = st.fonts.value.find((f) => f.id === st.fontId.value)
  if (own) return own.family
  return (
    BUILTIN_FONTS.find((f) => f.id === st.fontId.value)?.family ?? DEFAULT_FONT.family
  )
}

/**
 * Shrinks the photo before it goes out.
 *
 * 2400 px on the long edge: measured on three pages of Elmar's handwriting,
 * that is where the model still read every letter.
 */
async function downscale(file: File, max: number): Promise<{ data: string; type: string }> {
  const bmp = await createImageBitmap(file)
  const f = Math.min(1, max / Math.max(bmp.width, bmp.height))
  const c = document.createElement('canvas')
  c.width = Math.round(bmp.width * f)
  c.height = Math.round(bmp.height * f)
  c.getContext('2d')?.drawImage(bmp, 0, 0, c.width, c.height)
  const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.85))
  if (!blob) throw new Error('Das Bild ließ sich nicht verkleinern.')
  const buf = await blob.arrayBuffer()
  let bin = ''
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b)
  return { data: btoa(bin), type: 'image/jpeg' }
}
